import { t } from "@impero-com/common/utils/i18n";
import { notifyRequestError } from "@impero-com/common/utils/query_client";
import { emitNotification } from "@impero-com/ui";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import {
    getGetControlProgramsQueryKey,
    getGetControlProgramsControlProgramIdQueryKey,
    usePatchControlProgramsControlProgramId,
} from "api/generated/control-program/control-program";
import { getEntityCountQueryKey } from "api/generated/entities/entities";
import { ControlEntityConflict } from "api/generated/model";

export function useUpdateControlProgramMutation() {
    const queryClient = useQueryClient();
    return usePatchControlProgramsControlProgramId({
        mutation: {
            onSuccess: (_response, { controlProgramId, data }) => {
                // The edit page always navigates away (full-page reload to the
                // legacy view) after a successful update, which discards the SPA
                // query cache. Mark the queries stale with `refetchType: "none"`
                // so we don't kick off refetches that the navigation immediately
                // aborts.
                // If we migrate the control program overview page to
                // v2 perhaps we don't need this anymore.
                queryClient.invalidateQueries({
                    queryKey:
                        getGetControlProgramsControlProgramIdQueryKey(
                            controlProgramId
                        ),
                    refetchType: "none",
                });
                queryClient.invalidateQueries({
                    queryKey: getGetControlProgramsQueryKey(),
                    refetchType: "none",
                });
                Object.keys(data.controlEntityIds ?? {}).forEach((id) => {
                    queryClient.invalidateQueries({
                        queryKey: getEntityCountQueryKey(Number(id)),
                        refetchType: "none",
                    });
                });
            },
            onError: (error: AxiosError<ControlEntityConflict>) => {
                if (error?.response?.status === 409) {
                    const message =
                        error.response.data?.code === "ReferencedInDatasheet"
                            ? t("EntityView_DeleteAction_DataSheet_Conflict")
                            : t("EditControlProgram_Entity_Conflict");
                    emitNotification("error", message);
                    return;
                }
                notifyRequestError(error);
            },
        },
    });
}
